import React, { useState, useEffect } from 'react';
import { Stack, Button, FormControl } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { translate as t } from 'hooks/useTranslations';
import { PageToolbar } from '_lex/_atoms/PageToolbar';
import QuickSearch from 'components/_atoms/QuickSearch';
import debounce from 'lodash/debounce';
import ReactSelect, { components } from 'react-select';
import { compliancePerformanceColors } from '.';

interface ComplianceOption {
  value: string;
  label: string;
}

interface FiltersProps {
  initialSearch: string;
  initialDate: string;
  initialCompliance: string;
  updateParams: (params: any) => void;
  isFiltered: boolean;
  onClearFilters: () => void;
}

const Filters: React.FC<FiltersProps> = ({
  initialSearch,
  initialDate,
  initialCompliance,
  updateParams,
  isFiltered,
  onClearFilters,
}) => {
  const [search, setSearch] = useState(initialSearch);
  const [date, setDate] = useState<Dayjs | null>(initialDate ? dayjs(initialDate) : dayjs().subtract(1, 'day'));
  const [compliance, setCompliance] = useState(initialCompliance);

  useEffect(() => {
    setSearch(initialSearch);
    setDate(initialDate ? dayjs(initialDate) : dayjs().subtract(1, 'day'));
    setCompliance(initialCompliance);
  }, [initialSearch, initialDate, initialCompliance]);

  const debouncedSearchChange = React.useMemo(
    () => debounce((value: string) => updateParams({ search: value }), 300),
    [updateParams],
  );

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    debouncedSearchChange(value);
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    const resolved = newValue ?? dayjs().subtract(1, 'day');
    setDate(resolved);
    updateParams({ date: resolved.format('YYYY-MM-DD') });
  };

  const handleComplianceChange = (newValue: any) => {
    const value = newValue?.value ?? '';
    setCompliance(value);
    updateParams({ compliance: value });
  };

  const ComplianceSingleValue = ({ children, ...props }: any) => (
    <components.SingleValue {...props}>
      {t('dashboard.riskPool.filters.compliancePrefix')}: {children}
    </components.SingleValue>
  );

  const complianceOptions: ComplianceOption[] = [
    { value: '', label: t('dashboard.riskPool.filters.all') },
    ...Object.keys(compliancePerformanceColors).map((performance) => ({
      value: performance,
      label: t(`dashboard.riskPool.complianceOverview.${performance}`),
    })),
  ];

  return (
    <PageToolbar
      leftItems={
        <Stack direction='row' gap={2} alignItems='center'>
          <QuickSearch
            id='risk-pool-search'
            initialValue={search}
            onChange={handleSearchChange}
            placeholder={t('dashboard.riskPool.searchPlaceholder')}
          />
          <DatePicker
            value={date}
            onChange={handleDateChange}
            maxDate={dayjs().subtract(1, 'day')}
            slotProps={{
              textField: {
                size: 'small',
                InputProps: { size: 'small' },
                sx: { '& .MuiFormLabel-root': { display: 'none' }, pt: 0 },
              },
            }}
            label={false}
            sx={{ paddingTop: 0 }}
          />
          <FormControl sx={{ minWidth: 180, pt: 0 }}>
            <ReactSelect
              value={complianceOptions.find((option) => option.value === compliance)}
              options={complianceOptions}
              placeholder={`${t('dashboard.riskPool.filters.compliancePrefix')}:`}
              menuPortalTarget={document.body}
              components={{ SingleValue: ComplianceSingleValue }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, width: 240 }),
                input: (base) => ({
                  ...base,
                  boxShadow: 'none',
                  outline: 0,
                  minHeight: 26,
                }),
              }}
              menuPosition='fixed'
              menuPlacement='auto'
              isSearchable={false}
              onChange={handleComplianceChange}
            />
          </FormControl>
          {isFiltered && (
            <Button color='neutral' onClick={onClearFilters} size='small'>
              {t('dashboard.riskPool.actions.clearFilters')}
            </Button>
          )}
        </Stack>
      }
      rightItems={
        <Button variant='contained' size='small'>
          {t('dashboard.riskPool.actions.export')}
        </Button>
      }
    />
  );
};

export default Filters;
